/*
  Tabbed collections.

  Every panel is rendered server-side. This file only toggles visibility, so
  there is no fetch, no loading state and no URL change. Switching tabs cannot
  fail on a slow connection because nothing is being retrieved.

  Implements the WAI-ARIA tabs pattern with roving tabindex: exactly one tab is
  in the document tab order at a time, so Tab moves past the whole tablist
  rather than through every tab, and the arrow keys move within it.
*/
class TabbedCollections extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    if (this.tabs.length === 0) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    });
  }

  panelFor(tab) {
    return this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
  }

  select(tab, options) {
    const focusTab = !options || options.focus !== false;

    this.tabs.forEach((candidate) => {
      const isActive = candidate === tab;
      candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
      // Roving tabindex: only the selected tab stays reachable with Tab.
      candidate.setAttribute('tabindex', isActive ? '0' : '-1');
      candidate.classList.toggle('tabbed-collections__tab--active', isActive);

      const panel = this.panelFor(candidate);
      if (panel) panel.hidden = !isActive;
    });

    if (focusTab) tab.focus();
  }

  onKeydown(event) {
    const index = this.tabs.indexOf(event.target);
    if (index === -1) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
        next = this.tabs[(index + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
        next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        break;
      case 'Home':
        next = this.tabs[0];
        break;
      case 'End':
        next = this.tabs[this.tabs.length - 1];
        break;
      default:
        return;
    }

    // Only prevent default once a key we handle is confirmed, so Tab, Enter,
    // Space and browser shortcuts keep their normal behaviour.
    event.preventDefault();
    this.select(next);
  }
}

customElements.define('tabbed-collections', TabbedCollections);
